<script lang="ts" setup>
import { computed, ref, showErrorMsg, useRoute, watch } from '#imports';
import { useService } from '@kaokei/use-vue-service';
import { gatherCodeLevel, getYsInClass } from '@core/api/user';
import { ProfileService } from '@core/services/profile.service';
import { ZoneService } from '@core/services/zone.service';
import {
  CPP_PROGRAM_LEVEL_OPTIONS,
  PYTHON_PROGRAM_LEVEL_OPTIONS,
} from '@core/utils/constant';

const route = useRoute();
const service = useService(ProfileService);
const zoneService = useService(ZoneService);

const title = computed(() => {
  return zoneService.isPython()
    ? '你当前的Python水平是？'
    : '你当前的C++水平是？';
});

const options = computed(() => {
  return zoneService.isPython()
    ? PYTHON_PROGRAM_LEVEL_OPTIONS
    : CPP_PROGRAM_LEVEL_OPTIONS;
});

const loading = ref(false);
const closable = ref(false);
let ysInClassPromise: Promise<any> | null = null;

function handleSelectLevel(v: number) {
  service.programLevel = v;
}

function initYsInClass() {
  if (!ysInClassPromise) {
    ysInClassPromise = getYsInClass();
  }
  return ysInClassPromise;
}

function handleSubmit() {
  loading.value = true;
  gatherCodeLevel({ codeLevel: service.programLevel as number })
    .then(() => {
      service.setVisible(false);
      if (zoneService.isCpp()) {
        // 只针对c++域生效
        if (!(route.name === 'oj_contest_detail' && !!route.query.uid)) {
          // 排除掉csp模拟考专用链接
          initYsInClass().then((res) => {
            if (!res) {
              // 非在课用户才显示添加老师弹窗
              service.newUserAddTeacherVisible = true;
            }
            window.__bl.sum('oj_click_newuser_level_popup');
            window.hetaoTrack('oj_click_newuser_level_popup', {
              sub_type: `${service.programLevel}`,
              scene_id: res ? 1 : 2,
            });
          });
        } else {
          window.__bl.sum('oj_click_newuser_level_popup');
          window.hetaoTrack('oj_click_newuser_level_popup', {
            sub_type: `${service.programLevel}`,
            scene_id: 3, // csp模拟赛场景
          });
        }
      } else {
        window.__bl.sum('oj_click_newuser_level_popup');
        window.hetaoTrack('oj_click_newuser_level_popup', {
          sub_type: `${service.programLevel}`,
        });
      }
    })
    .catch((e) => {
      showErrorMsg(e);
      closable.value = true;
    })
    .finally(() => {
      loading.value = false;
    });
}

watch(
  () => service.programLevelVisible,
  (visible) => {
    if (visible) {
      initYsInClass().then((res) => {
        window.__bl.sum('oj_show_newuser_level_popup');
        window.hetaoTrack('oj_show_newuser_level_popup', {
          sub_type: `${service.programLevel}`,
          scene_id: res ? 1 : 2,
        });
      });
    }
  },
  { immediate: true },
);
</script>

<template>
  <a-modal
    v-model:open="service.programLevelVisible"
    :width="380"
    :footer="null"
    :closable="closable"
    :mask-closable="false"
    :destroy-on-close="true"
    :body-style="{ padding: '24px 40px 32px' }"
    :centered="true"
  >
    <h2 class="text-font1 mb-6 text-center text-base font-bold">
      {{ title }}
    </h2>

    <div class="grid grid-cols-2 gap-5">
      <div
        v-for="item in options"
        :key="item.value"
        class="flex-center h-12 w-full cursor-pointer rounded-sm border border-[#D9D9D9] hover:border-[#C1C1C1]"
        :class="{ '!border-primary': item.value === service.programLevel }"
        @click="handleSelectLevel(item.value)"
      >
        {{ item.label }}
      </div>
    </div>

    <div class="flex-center">
      <a-button
        :disabled="!service.programLevel"
        :loading="loading"
        type="primary"
        size="large"
        class="disabled-opacity mt-6 w-40"
        @click="handleSubmit"
        >确定</a-button
      >
    </div>
  </a-modal>
</template>

<style scoped></style>
